import React, { createContext } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { gql, useMutation, useQuery } from '@apollo/client'
import { media } from 'helpers'
import styled from 'styled-components'

import GoUpArrow from 'icons/go-up-arrow.svg'

import NestedNavItem from './NestedNavItem'
import NestedNavList from './NestedNavList'

export const NavContext = createContext()

const NestedNavigation = styled.div`
    position: relative;
    background-color: white;
    width: 260px;
    flex-shrink: 0;
    padding: 32px 0 20px;

    ${media.mobilePortrait`
        width: 100%;
        flex-direction: column;
        border-bottom: 1px solid rgba(0,0,0,0.21);
        padding: 20px 0;
    `};

    ${media.mobileLandscapeUp`
        border-right: 1px solid rgba(0,0,0,0.21);
    `}
`

const Content = styled.div`
    ${media.mobileLandscapeUp`
        position: sticky;
        top: 60px;
    `}
`

const NestedNav = ({ viewer, editMode, entityType, guid, ...rest }) => {
    const { t } = useTranslation()

    const typeName = entityType === 'wiki' ? 'Wiki' : 'Page'
    const query = entityType === 'wiki' ? WikiQuery : PageQuery

    const { loading, data } = useQuery(query, {
        variables: {
            guid,
        },
        fetchPolicy: 'cache-and-network',
    })

    const [mutate] = useMutation(MUTATION)

    if (loading || !data?.entity) return <NestedNavigation />

    const onDragEnd = (result) => {
        if (!result.destination) return

        const sourcePosition = result.source.index
        const destinationPosition = result.destination.index

        const newChildren = [...children]
        const [movedPage] = newChildren.splice(sourcePosition, 1)
        newChildren.splice(destinationPosition, 0, movedPage)

        mutate({
            variables: {
                input: {
                    guid: result.draggableId,
                    sourcePosition,
                    destinationPosition,
                },
            },
            optimisticResponse: {
                reorder: {
                    __typename: 'reorderPayload',
                    container: {
                        __typename: typeName,
                        guid,
                        children: newChildren,
                    },
                },
            },
        })
    }

    const { entity } = data
    const { children, parent, rootParent } = entity

    const grandParent = parent?.parent
    const highestParent = grandParent || parent || entity

    const root = rootParent || highestParent

    return (
        <NestedNavigation {...rest} className="NestedNavigation">
            <Content>
                {root && (
                    <NestedNavItem isRoot as={NavLink} to={root.url} end>
                        {root.title}
                    </NestedNavItem>
                )}

                {grandParent && grandParent?.guid !== root.guid && (
                    <NestedNavItem as={NavLink} to={grandParent.url}>
                        <GoUpArrow style={{ marginRight: '6px' }} />
                        {t('entity-wiki.go-up')}
                    </NestedNavItem>
                )}

                <NavContext.Provider
                    value={{
                        activeGuid: guid,
                        editMode,
                        canWriteToContainer: viewer.canWriteToContainer,
                        onDragEnd,
                    }}
                >
                    <NestedNavList entity={highestParent} indentLevel={0} />
                </NavContext.Provider>
            </Content>
        </NestedNavigation>
    )
}

const MUTATION = gql`
    mutation NestedNavMutation($input: reorderInput!) {
        reorder(input: $input) {
            container {
                guid
                ... on Wiki {
                    children {
                        guid
                        title
                        url
                    }
                }
                ... on Page {
                    children {
                        guid
                        title
                        url
                    }
                }
            }
        }
    }
`

const getEntityFragment = (entityType) => {
    const wikiNavFragment = `
        guid
        title
        url
        canEdit
        hasChildren
    `
    const pageNavFragment = `
        guid
        title
        url
        canEdit
        hasChildren
    `

    const navFragment =
        entityType === 'wiki' ? wikiNavFragment : pageNavFragment

    return `
        title
        canEdit
        url
        inGroup
        statusPublished
        rootParent {
            ${navFragment}
        }
        parent {
            ${navFragment}
            parent {
                ${navFragment}
                children {
                    ${navFragment}
                    children {
                        ${navFragment}
                        children {
                            ${navFragment}
                        }
                    }
                }
            }
            children {
                ${navFragment}
                children {
                    ${navFragment}
                }
            }
        }
        children {
            ${navFragment}
        }
    `
}

const WikiQuery = gql`
    query Item($guid: String!) {
        entity(guid: $guid) {
            guid
            ... on Wiki {
                ${getEntityFragment('wiki')}
            }
        }
    }
`

const PageQuery = gql`
    query Item($guid: String) {
        entity(guid: $guid) {
            guid
            ... on Page {
                ${getEntityFragment('page')}
            }
        }
    }
`

export default NestedNav
